import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ProjectListRow, ProjectsResponse } from '../api/client.js';
import { fetchProjects } from '../api/client.js';
import { CreateProjectForm } from '../components/CreateProjectForm.js';
import { Stamp } from '../components/Stamp.js';
import { useDictionary } from '../i18n/context.js';
import type { Dictionary } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// Projects list (UI structure spec 13.1): every project in the workspace,
// each with name, website, connection states, automation mode/paused
// state, upcoming publication, and attention count. Actions are Create
// project and Open project; deletion is deliberately absent (13.1: account,
// publication, and audit retention rules are undefined).
//
// Create project: the five-step wizard (13.2) is out of this plan's scope,
// but `CreateProjectForm` -- the plain form `POST /api/projects` already
// accepts (whole-branch review finding I3) -- already exists and is live on
// Home. Reusing it here rather than disabling the action keeps the two
// screens consistent: a control that creates a project on Home and refuses
// to on Projects would be a worse experience than either extreme alone. See
// the task-4-6 report for the fuller reasoning.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: ProjectsResponse };

const CONNECTION_PLATFORMS = ['x', 'pinterest'] as const;

// Duplicated from home.tsx deliberately -- route files do not share UI
// helpers in this codebase (home.tsx's own comment on this exact ternary).
const platformLabel = (platform: 'x' | 'pinterest'): string => (platform === 'pinterest' ? 'Pinterest' : 'X');

const AUTOMATION_MODE_KEY: Record<string, keyof Dictionary['automationModes']> = {
  manual: 'manual',
  guarded_auto: 'guardedAuto',
  autonomous: 'autonomous',
};

// Paused overrides the underlying mode's own label -- a project can be
// "guarded_auto" and paused at the same time, and what an operator needs to
// see first is that nothing will publish right now, not which mode it will
// resume in. Exported and tested directly (the way `connectionView` in
// project.tsx is), since this is the one non-trivial decision this screen
// makes.
export const automationStatusLabel = (dict: Dictionary, project: { automationMode: string; paused: boolean }): string => {
  if (project.paused) return dict.automationModes.automationPaused;
  const key = AUTOMATION_MODE_KEY[project.automationMode] ?? 'manual';
  return dict.automationModes[key];
};

const ProjectTableRow = ({ project }: { project: ProjectListRow }): React.JSX.Element => {
  const dict = useDictionary();

  return (
    <tr data-testid={`project-row-${project.id}`}>
      <td>
        <div className="stack-1">
          <span className="strong">{project.name}</span>
          {project.website && <span className="mono-meta">{project.website}</span>}
        </div>
      </td>
      <td>
        <div className="row wrap" style={{ gap: 'var(--sp-2)' }}>
          {CONNECTION_PLATFORMS.map((platform) => {
            const state = project.connections.find((c) => c.platform === platform)?.state ?? null;
            const connected = state === 'connected';
            return (
              <span key={platform} className={connected ? 'chip chip--done' : 'chip'}>
                {platformLabel(platform)}: {connected ? dict.ui.project.connected : dict.ui.home.notConnected}
              </span>
            );
          })}
        </div>
      </td>
      <td><span className="t-meta">{automationStatusLabel(dict, project)}</span></td>
      <td>
        {project.nextPublishAtUtc
          ? <Stamp instant={project.nextPublishAtUtc} compact />
          : <span className="t-meta">{dict.ui.projects.noUpcoming}</span>}
      </td>
      <td className="num">{project.attentionCount}</td>
      <td><Link className="btn btn--ghost" to={`/projects/${project.id}`}>{dict.ui.projects.open}</Link></td>
    </tr>
  );
};

export const ProjectsRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [showCreateForm, setShowCreateForm] = useState(false);

  const load = (): void => {
    setState({ status: 'loading' });
    fetchProjects()
      .then((data) => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, []);

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="between">
        <h1 className="t-title">{dict.ui.projects.title}</h1>
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => { setShowCreateForm((v) => !v); }}
          >
            {dict.ui.home.createProject}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <CreateProjectForm
          onCreated={(projectId) => { setShowCreateForm(false); navigate(`/projects/${projectId}`); }}
          onCancel={() => { setShowCreateForm(false); }}
        />
      )}

      {state.status === 'loading' && <p className="t-meta">{dict.ui.projects.loading}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.projects.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.projects.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && (
        <div className="panel">
          <div className="panel__body panel__body--flush">
            {state.data.projects.length === 0
              ? (
                <div className="empty">
                  <div className="empty__title">{dict.ui.projects.noProjects}</div>
                  <div className="empty__body">{dict.ui.projects.noProjectsBody}</div>
                </div>
              )
              : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>{dict.ui.projects.nameColumn}</th>
                      <th>{dict.ui.projects.connectionsColumn}</th>
                      <th>{dict.ui.projects.automationColumn}</th>
                      <th>{dict.ui.projects.nextPublishColumn}</th>
                      <th className="num">{dict.ui.projects.attentionColumn}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.projects.map((project) => <ProjectTableRow key={project.id} project={project} />)}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
